<template>
  <v-container fluid id="lateOrders" class="pa-0 pa-sm-3">
    <v-row wrap justify="center">
      <v-col cols="12" xs="12" class="headline pb-0">
        Late Truck Request Orders
        <span class="caption grey--text d-block">
          Showing orders placed after 12:30 PM for next-day service in the last
          30 days
        </span>
      </v-col>

      <!-- Dashboard Stats -->
      <v-col cols="12" xs="12" class="mb-1">
        <v-row v-if="loading">
          <!-- Skeleton loaders for count cards -->
          <v-col cols="12" sm="6" md="2">
            <v-skeleton-loader
              type="card"
              class="text-center"
              height="120"
            ></v-skeleton-loader>
          </v-col>
          <v-col v-for="n in 5" :key="n" cols="12" sm="6" md="2">
            <v-skeleton-loader
              type="card"
              class="text-center"
              height="120"
            ></v-skeleton-loader>
          </v-col>
        </v-row>
        <v-row v-else>
          <!-- Total Late Orders Card -->
          <v-col cols="12" sm="6" md="2">
            <v-card elevation="2" class="text-center">
              <v-card-title class="justify-center pb-1 text-h5">{{
                totalLateOrders
              }}</v-card-title>
              <v-card-text class="pt-0">
                <div class="subtitle-2 font-weight-bold text-truncate">
                  Total Late Orders
                </div>
                <div class="caption text-truncate">in last 30 days</div>
              </v-card-text>
            </v-card>
          </v-col>

          <!-- Top Requesters Cards -->
          <v-col
            v-for="(requester, index) in topRequesters"
            :key="index"
            cols="12"
            sm="6"
            md="2"
          >
            <v-card
              elevation="2"
              class="text-center"
              :class="index === 0 ? 'red lighten-5' : ''"
            >
              <v-card-title class="justify-center pb-1 text-h5">
                {{ requester.count }}
              </v-card-title>
              <v-card-text class="pt-0">
                <div class="subtitle-2 font-weight-bold text-truncate">
                  {{ requester.name }}
                </div>
                <div class="caption text-truncate">{{ requester.email }}</div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-col>

      <v-col cols="12" xs="12" class="">
        <v-card v-if="loading">
          <v-skeleton-loader type="table" class="mx-auto"></v-skeleton-loader>
        </v-card>
        <v-card v-else>
          <v-data-table
            :items="lateOrders"
            :headers="headers"
            :items-per-page="20"
            sort-by="orderDate"
            sort-desc
          >
            <template v-slot:top>
              <v-toolbar flat color="white" class="pt-1">
                <v-text-field
                  outlined
                  dense
                  hide-details
                  v-model="search"
                  append-icon="search"
                  label="Filter by name"
                ></v-text-field>
              </v-toolbar>
            </template>
            <template v-slot:item.orderNumber="{ item }">
              <span class="subtitle-2">#{{ item.orderNumber }}</span>
            </template>
            <template v-slot:item.orderDate="{ item }">
              <span
                :class="isLateOrder(item) ? 'red--text font-weight-bold' : ''"
              >
                {{ item.orderDate.toDate() | moment("MMM Do h:mma") }}
              </span>
            </template>
            <template v-slot:item.orderedBy="{ item }">
              <span
                :class="isLateOrder(item) ? 'red--text font-weight-bold' : ''"
              >
                {{ item.orderedBy.firstName }} {{ item.orderedBy.lastName }}
              </span>
            </template>
            <template v-slot:item.jobSite.street="{ item }">
              {{ item.jobSite.street }}
              <span class="caption">({{ item.jobSite.jobId }})</span>
            </template>
            <template v-slot:item.requestType="{ item }">
              <truck-request-details :showNotes="false" :order="item" />
            </template>
          </v-data-table>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import fb from "@/main";
import { mapState } from "vuex";
import moment from "moment";
import truckRequestDetails from "@/components/truckRequests/truckRequestDetails";

export default {
  components: {
    truckRequestDetails,
  },
  data() {
    return {
      search: "",
      loading: true,
      headers: [
        {
          text: "Order",
          align: "left",
          value: "orderNumber",
          sortable: true,
          width: 80,
        },
        { text: "Order Date/Time", value: "orderDate", width: 180 },
        { text: "Requested By", value: "orderedBy", width: 180 },
        { text: "Job", value: "jobSite.street" },
        { text: "Request Type", value: "requestType" },
      ],
      orders: [],
    };
  },
  computed: {
    ...mapState(["user", "truckRequests"]),
    lateOrders() {
      const filteredOrders = this.orders.filter(
        (order) => this.isLateOrder(order) && !this.isAdminUser(order)
      );

      if (this.search) {
        const searchTerms = this.search.toLowerCase().trim();
        const normalizedSearchTerms = this.normalizeText(searchTerms);

        return filteredOrders.filter((order) => {
          if (!order.orderedBy) return false;

          const firstName = order.orderedBy.firstName.toLowerCase();
          const lastName = order.orderedBy.lastName.toLowerCase();
          const fullName = `${firstName} ${lastName}`;

          const normalizedFirstName = this.normalizeText(firstName);
          const normalizedLastName = this.normalizeText(lastName);
          const normalizedFullName = this.normalizeText(fullName);

          return (
            normalizedFirstName.includes(normalizedSearchTerms) ||
            normalizedLastName.includes(normalizedSearchTerms) ||
            normalizedFullName.includes(normalizedSearchTerms)
          );
        });
      }

      return filteredOrders;
    },

    // Get top 5 requesters with late order counts
    topRequesters() {
      // Get all late orders
      const lateOrdersOnly = this.orders.filter(
        (order) => this.isLateOrder(order) && !this.isAdminUser(order)
      );

      // Group orders by requester name
      const requesterCounts = {};

      lateOrdersOnly.forEach((order) => {
        if (!order.orderedBy) return;

        const fullName = `${order.orderedBy.firstName} ${order.orderedBy.lastName}`;
        if (!requesterCounts[fullName]) {
          requesterCounts[fullName] = {
            name: fullName,
            count: 0,
            email: order.orderedBy.email || "",
            uid: order.orderedBy.uid || "",
          };
        }
        requesterCounts[fullName].count++;
      });

      // Convert to array and sort by count
      const sortedRequesters = Object.values(requesterCounts)
        .sort((a, b) => b.count - a.count)
        .slice(0, 5); // Get top 5

      return sortedRequesters;
    },

    // Total count of late orders
    totalLateOrders() {
      return this.orders.filter(
        (order) => this.isLateOrder(order) && !this.isAdminUser(order)
      ).length;
    },
  },
  async created() {
    // Always fetch the latest 30 days of data directly from Firestore
    this.fetchLateOrders();
  },
  methods: {
    processOrdersFromStore() {
      // Get the date 30 days ago
      const thirtyDaysAgo = moment()
        .subtract(30, "days")
        .toDate();

      // Filter orders from the last 30 days
      this.orders = this.truckRequests.filter((order) => {
        if (!order.orderDate) return false;
        return order.orderDate.toDate() >= thirtyDaysAgo;
      });

      this.loading = false;
    },
    async fetchLateOrders() {
      try {
        const thirtyDaysAgo = moment()
          .subtract(30, "days")
          .toDate();

        const ordersRef = await fb.db
          .collection("truckRequests")
          .where("orderDate", ">=", thirtyDaysAgo)
          .orderBy("orderDate", "desc")
          .get();

        this.orders = [];
        ordersRef.forEach((doc) => {
          this.orders.push({
            id: doc.id,
            ...doc.data(),
          });
        });

        this.loading = false;
      } catch (error) {
        console.error("Error fetching late orders:", error);
        this.loading = false;
      }
    },
    isLateOrder(order) {
      if (!order.orderDate || !order.requestDate) return false;

      const orderTime = moment(order.orderDate.toDate());
      const orderHour = orderTime.hours();
      const orderMinute = orderTime.minutes();

      // Format the order date to YYYY-MM-DD for comparison
      const orderDateFormatted = orderTime.format("YYYY-MM-DD");

      // Check if order was placed the day before the requested date
      // AND if it was placed after 12:30 PM
      return (
        // Order date should be one day before the request date
        moment(orderDateFormatted)
          .add(1, "days")
          .format("YYYY-MM-DD") === order.requestDate &&
        // Order time should be after 12:30 PM
        (orderHour > 12 || (orderHour === 12 && orderMinute >= 30))
      );
    },

    // Check if the order is from an admin user to be excluded
    isAdminUser(order) {
      if (!order.orderedBy) return false;

      const fullName = `${order.orderedBy.firstName} ${order.orderedBy.lastName}`;

      return (
        fullName === "Alan Byrne" ||
        fullName === "Mike Perugini" ||
        fullName === "Víctor Espinoza "
      );
    },

    // Normalize text by removing accents for search comparison
    normalizeText(text) {
      return text.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
    },
  },
};
</script>

<style scoped>
.v-data-table ::v-deep tbody tr:hover {
  background-color: #f5f5f5 !important;
}
</style>
